/**
 * Topics Page
 *
 * Browse tasks organized by hierarchical topics and manage supporting tasks.
 * Shows both:
 * - Tasks organized by topicPath (hierarchical container)
 * - Supporting tasks related to topics (via RELATED_TO edges)
 */

import { useState } from 'react';
import { FolderTree, Tag, X, Loader2, Pencil } from 'lucide-react';
import { useTasks, useTask, useTaskMutations } from '@/hooks/use-tasks';
import { useTopics, useSupportingTasks, useTaskTopicMutations } from '@/hooks/use-topics';
import { TopicTree } from '@/components/tasks/topic-tree';
import { TaskList, TaskEditModal } from '@/components/tasks';
import { Card, CardContent } from '@/components/ui';
import type { Task, UpdateTaskInput } from '@/types';

/**
 * Supporting Tasks Panel
 * Shows tasks that are related to a topic (via RELATED_TO edges)
 */
function SupportingTasksPanel({ 
  topicId, 
  onEditTask,
}: { 
  topicId: string;
  onEditTask: (task: Task) => void;
}) {
  const { data: supportingData, isLoading } = useSupportingTasks(topicId);
  const { removeSupportingTask } = useTaskTopicMutations();
  const [removingTaskId, setRemovingTaskId] = useState<string | null>(null);

  const handleRemove = async (taskId: string) => {
    setRemovingTaskId(taskId);
    try {
      await removeSupportingTask.mutateAsync({ topicId, taskId });
    } catch (error) {
      console.error('Failed to remove supporting task:', error);
    } finally {
      setRemovingTaskId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading supporting tasks...
      </div>
    );
  }

  const supportingTasks = supportingData?.tasks ?? [];

  if (supportingTasks.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-4 text-center">
        No supporting tasks for this topic.
        <p className="text-xs mt-1">
          Use the task edit dialog to relate tasks to this topic.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {supportingTasks.map(({ taskId }) => (
        <SupportingTaskItem
          key={taskId}
          taskId={taskId}
          onRemove={() => handleRemove(taskId)}
          isRemoving={removingTaskId === taskId}
          onEdit={onEditTask}
        />
      ))}
    </div>
  );
}

/**
 * Individual supporting task item with fetch
 */
function SupportingTaskItem({
  taskId,
  onRemove,
  isRemoving,
  onEdit,
}: {
  taskId: string;
  onRemove: () => void;
  isRemoving: boolean;
  onEdit: (task: Task) => void;
}) {
  const { data: taskData, isLoading } = useTask(taskId);
  const task = taskData?.task;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg animate-pulse">
        <div className="h-4 bg-gray-200 rounded flex-1" />
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
        {task.dueAt && (
          <p className="text-xs text-gray-500">
            Due: {new Date(task.dueAt).toLocaleDateString()}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(task)}
          className="p-1 text-gray-400 hover:text-primary-600 rounded"
          title="Edit task"
        >
          <Pencil className="w-4 h-4" />
        </button>
        <button
          onClick={onRemove}
          disabled={isRemoving}
          className="p-1 text-gray-400 hover:text-red-600 rounded disabled:opacity-50"
          title="Remove from this topic"
        >
          {isRemoving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <X className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}

/**
 * Topic detail panel showing supporting tasks
 */
function TopicDetailPanel({
  topicId,
  onEditTask,
}: {
  topicId: string;
  onEditTask: (task: Task) => void;
}) {
  const { data: topicsData } = useTopics();
  const topic = topicsData?.topics?.find(t => t.id === topicId);

  if (!topic) {
    return null;
  }

  return (
    <Card className="mt-4">
      <div className="p-4 border-b border-gray-200 bg-blue-50">
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold text-gray-900">{topic.name}</h3>
            <p className="text-xs text-gray-500">
              Supporting tasks (related via work graph)
            </p>
          </div>
        </div>
      </div>
      <CardContent className="p-4">
        <SupportingTasksPanel 
          topicId={topicId} 
          onEditTask={onEditTask}
        />
      </CardContent>
    </Card>
  );
}

export function TopicsPage() {
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Fetch all tasks for the topic tree
  const { data: allTasksData, isLoading: allLoading } = useTasks({});

  // Fetch filtered tasks when a topic is selected
  const { data: filteredData, isLoading: filteredLoading } = useTasks(
    selectedTopic ? { topic: selectedTopic } : {}
  );

  // Fetch topics for the detail panel
  const { data: topicsData, isLoading: topicsLoading } = useTopics();

  // Task mutations
  const { updateTask, completeTask, dismissTask, convertToReference } = useTaskMutations();

  const allTasks = allTasksData?.tasks ?? [];
  const displayedTasks = selectedTopic
    ? (filteredData?.tasks ?? [])
    : allTasks;
  const isLoading = allLoading || (selectedTopic && filteredLoading);

  // Handle topic selection - find matching topic entity
  const handleTopicSelect = (topicPath: string | null) => {
    setSelectedTopic(topicPath);
    
    // Try to find a matching topic entity by path or name
    if (topicPath && topicsData?.topics) {
      const matchingTopic = topicsData.topics.find(
        t => t.path === topicPath || t.name === topicPath || t.slug === topicPath.toLowerCase().replace(/\//g, '-')
      );
      setSelectedTopicId(matchingTopic?.id ?? null);
    } else {
      setSelectedTopicId(null);
    }
  };

  // Handle edit task
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
  };

  const handleSaveTask = (taskId: string, updates: UpdateTaskInput) => {
    updateTask.mutate(
      { taskId, input: updates },
      {
        onSuccess: () => {
          setEditingTask(null);
        },
      }
    );
  };

  const handleCompleteTask = (taskId: string) => {
    completeTask.mutate(taskId);
    setEditingTask(null);
  };

  const handleDismissTask = (taskId: string) => {
    dismissTask.mutate(taskId);
    setEditingTask(null);
  };

  const handleConvertToReference = (taskId: string) => {
    convertToReference.mutate(taskId);
    setEditingTask(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <FolderTree className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Topics</h1>
            <p className="text-gray-600 mt-1">
              Browse tasks organized by topic hierarchy
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar - Topic Tree */}
        <div className="lg:col-span-4 xl:col-span-3">
          {allLoading ? (
            <Card>
              <CardContent className="p-4">
                <div className="animate-pulse space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="h-8 bg-gray-200 rounded" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : (
            <TopicTree
              tasks={allTasks}
              onTopicSelect={handleTopicSelect}
              selectedTopic={selectedTopic}
            />
          )}

          {/* Topics List (from Work Graph) */}
          {!topicsLoading && topicsData?.topics && topicsData.topics.length > 0 && (
            <Card className="mt-4">
              <div className="p-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Tag className="w-4 h-4 text-blue-600" />
                  Work Graph Topics
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Topics with task relationships
                </p>
              </div>
              <div className="p-2 max-h-[200px] overflow-y-auto">
                {topicsData.topics.map((topic) => (
                  <button
                    key={topic.id}
                    onClick={() => {
                      setSelectedTopicId(topic.id);
                      setSelectedTopic(topic.path || topic.name);
                    }}
                    className={`
                      w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors
                      ${selectedTopicId === topic.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'hover:bg-gray-100'
                      }
                    `}
                  >
                    <Tag className="w-4 h-4 text-blue-500" />
                    <span className="flex-1 text-sm font-medium truncate">{topic.name}</span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Main - Task List & Supporting Tasks */}
        <div className="lg:col-span-8 xl:col-span-9">
          {/* Tasks in Topic (by path) */}
          <Card>
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedTopic || 'All Topics'}
              </h2>
              <p className="text-sm text-gray-500">
                {displayedTasks.length} task{displayedTasks.length !== 1 ? 's' : ''} in this topic
              </p>
            </div>
            <CardContent className="p-0">
              <TaskList
                tasks={displayedTasks}
                isLoading={!!isLoading}
                onEdit={handleEditTask}
                emptyMessage={
                  selectedTopic
                    ? `No tasks in "${selectedTopic}"`
                    : 'No tasks found'
                }
              />
            </CardContent>
          </Card>

          {/* Supporting Tasks Panel (Work Graph) */}
          {selectedTopicId && (
            <TopicDetailPanel 
              topicId={selectedTopicId}
              onEditTask={handleEditTask}
            />
          )}
        </div>
      </div>

      {/* Edit Task Modal */}
      {editingTask && (
        <TaskEditModal
          task={editingTask}
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          onSave={handleSaveTask}
          isSaving={updateTask.isPending}
          onComplete={handleCompleteTask}
          onDismiss={handleDismissTask}
          onConvertToReference={handleConvertToReference}
        />
      )}
    </div>
  );
}

